import React from 'react'
import * as Yup from 'yup'
import { Formik } from 'formik'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'

import ImageField from '../../FormLegacy/FormFieldsV2/ImageField'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import PhoneField from '../../FormLegacy/FormFieldsV2/PhoneField'
import RadioField from '../../FormLegacy/FormFieldsV2/RadioField'
import { getAssetLink } from '../../../helpers/get-asset-link'
import { formValidation } from '../../FormLegacy/validation'

const validationSchema = Yup.object({
  firstName: formValidation.required,
  lastName: formValidation.required,
  email: formValidation.email,
  phone: formValidation.phone,
  notifications: formValidation.required,
})

const staffCategories = [
  'Plumbing',
  'Lighting',
  'Windows',
  'HVAC',
  'Doors',
  'Floors',
  'Appliances',
  'Other',
]

const MaintenanceStaffForm = ({
  selectedMember,
  onSubmit,
  errorMessage,
  onDeleteStaff,
  isDeletePending,
}) => {
  const { t } = useTranslation(['maintenance', 'common'])
  const { staffId } = useParams()

  const isAddingNewStaff = React.useMemo(() => {
    return staffId === '0'
  }, [staffId])

  return (
    <Formik
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={{
        firstName: selectedMember && selectedMember.user ? selectedMember.user.firstName : '',
        lastName: selectedMember && selectedMember.user ? selectedMember.user.lastName : '',
        email: selectedMember && selectedMember.user ? selectedMember.user.email : '',
        category:
          selectedMember && !isAddingNewStaff
            ? selectedMember.category
            : isAddingNewStaff
              ? staffCategories
              : '',
        phone: selectedMember ? selectedMember.phone : '',
        notifications: selectedMember ? selectedMember.notifications : 'All',
        avatar: {
          file: false,
          image: getAssetLink({ path: 'chitown-avatar/avatar', file: selectedMember?.user.avatar }),
        },
      }}
      onSubmit={onSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        dirty,
      }) => {
        return (
          <div className="maintenance-staff-form">
            <div className="maintenance-staff-form__head">
              <div className="maintenance-staff-form__avatar">
                <ImageField
                  name="image"
                  imageRatio={1}
                  value={values.avatar}
                  additionalClass={'maintenance-staff__avatar'}
                  placeholderImage={getAssetLink({
                    path: 'chitown-avatar/avatar',
                    file: 'avatar.png',
                  })}
                  onChange={(e) => setFieldValue('avatar', { image: e.target.value, file: true })}
                />

                <div className={'maintenance-staff__avatar-text'}>
                  <strong>{`${values.firstName} ${values.lastName}`}</strong>
                </div>
              </div>
            </div>

            <br />
            <div className={'maintenance-staff-form-divider'} />
            <br />

            <div className={'maintenance-staff-form-details-title'}>
              <b>{t('maintenance:staff.form.changeInfo')}</b>
            </div>
            <br />

            <label htmlFor="eventName" className="maintenance-staff__label">
              {t('maintenance:staff.form.firstName.label')}
            </label>

            <TextField
              name="firstName"
              placeholder=""
              onChange={handleChange}
              value={values.firstName}
              onBlur={() => setFieldTouched('password')}
              isError={touched.firstName && !!errors.firstName}
              errorLabel={touched.firstName && errors.firstName}
            />

            <label htmlFor="eventName" className="maintenance-staff__label">
              {t('maintenance:staff.form.lastName.label')}
            </label>

            <TextField
              name="lastName"
              placeholder=""
              onChange={handleChange}
              value={values.lastName}
              onBlur={() => setFieldTouched('lastName')}
              isError={touched.lastName && !!errors.lastName}
              errorLabel={touched.lastName && errors.lastName}
            />

            <label htmlFor="eventName" className="maintenance-staff__label">
              {t('maintenance:staff.form.phone.label')}
            </label>

            <PhoneField
              type="number"
              name="phone"
              placeholder=""
              value={values.phone}
              onChange={handleChange}
              onBlur={() => setFieldTouched('phone')}
              isError={touched.phone && !!errors.phone}
              errorLabel={touched.phone && errors.phone}
            />

            <label htmlFor="eventName" className="maintenance-staff__label">
              {t('maintenance:staff.form.email.label')}
            </label>

            <TextField
              type="email"
              name="email"
              placeholder=""
              value={values.email}
              onChange={handleChange}
              onBlur={() => setFieldTouched('email')}
              isError={touched.email && !!errors.email}
              errorLabel={touched.email && errors.email}
            />

            <br />
            <div className={'maintenance-staff-form-divider'} />
            <br />

            <div className={'maintenance-staff-form-details-title'}>
              <b>{t('maintenance:staff.form.notifications.title')}</b>
            </div>
            <br />

            <RadioField
              value="All"
              name="notifications"
              onChange={handleChange}
              checked={values.notifications === 'All'}
              isError={touched.notifications && !!errors.notifications}
              label={
                <span className="maintenance-staff__radio-label">
                  {t('maintenance:staff.form.notifications.all')}
                </span>
              }
            />

            <RadioField
              value="Assigned"
              name="notifications"
              onChange={handleChange}
              checked={values.notifications === 'Assigned'}
              isError={touched.notifications && !!errors.notifications}
              label={
                <span className="maintenance-staff__radio-label">
                  {t('maintenance:staff.form.notifications.onlyAssigned')}
                </span>
              }
            />

            {errorMessage && (
              <div className="maintenance-staff__form-error">
                <p className={`help is-danger has-text-centered`}>{errorMessage}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || !dirty}
              className={`button button-primary is-block maintenance-staff__submit-btn ${
                isSubmitting ? 'is-loading' : ''
              }`}
              onClick={handleSubmit}
            >
              {isAddingNewStaff
                ? t('maintenance:staff.form.submit.saveNew')
                : t('maintenance:staff.form.submit.saveChanges')}
            </button>

            {selectedMember && (
              <button
                type="submit"
                disabled={isDeletePending}
                className={`button is-dark is-block maintenance-staff__delete-btn ${
                  isDeletePending ? 'is-loading' : ''
                }`}
                onClick={onDeleteStaff}
              >
                {t('common:actions.delete')}
              </button>
            )}
          </div>
        )
      }}
    </Formik>
  )
}

MaintenanceStaffForm.propTypes = {
  onSubmit: PropTypes.func,
  errorMessage: PropTypes.string,
  onDeleteStaff: PropTypes.func,
  isDeletePending: PropTypes.bool,
  selectedMember: PropTypes.object,
  location: PropTypes.object.isRequired,
}

export default connect()(MaintenanceStaffForm)
